import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  PagedResult, PagedRequest,
  PatientFilter, AppointmentFilter,
  PaymentFilter, AdminUserFilter, AdminProfFilter, SubscriptionFilter
} from '../models/pagination.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiService {

  private readonly BASE = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ─── helpers ──────────────────────────────────────────────────
  private params(obj: Record<string, any>): HttpParams {
    let p = new HttpParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p = p.set(k, String(v));
    });
    return p;
  }

  // ─── AUTH ──────────────────────────────────────────────────────
  register(data: any)        { return this.http.post(`${this.BASE}/auth/register`, data); }
  login(data: any)           { return this.http.post(`${this.BASE}/auth/login`, data); }
  googleLogin(token: string) { return this.http.post(`${this.BASE}/auth/google-login`, { token }); }
  forgotPassword(email: string) { return this.http.post(`${this.BASE}/auth/forgot-password`, { email }); }
  resetPassword(token: string, newPassword: string) { return this.http.post(`${this.BASE}/auth/reset-password`, { token, newPassword }); }

  // ─── PROFESSIONALS ─────────────────────────────────────────────
  getProfessionals(): Observable<any[]>           { return this.http.get<any[]>(`${this.BASE}/professionals`); }
  getProfessional(id: number): Observable<any>    { return this.http.get<any>(`${this.BASE}/professionals/${id}`); }
  createProfessional(d: any): Observable<any>     { return this.http.post<any>(`${this.BASE}/professionals`, d); }
  updateProfessional(id: number, d: any)          { return this.http.put(`${this.BASE}/professionals/${id}`, d); }
  toggleProfessional(id: number, isActive: boolean) { return this.http.put(`${this.BASE}/professionals/${id}/toggle-status`, { isActive }); }
  deleteProfessional(id: number)                  { return this.http.delete(`${this.BASE}/professionals/${id}`); }

  getBilling(profId: number): Observable<any>     { return this.http.get<any>(`${this.BASE}/professionals/${profId}/billing`); }
  saveBilling(profId: number, d: any)             { return this.http.post(`${this.BASE}/professionals/${profId}/billing`, d); }
  getSubscription(profId: number): Observable<any> { return this.http.get<any>(`${this.BASE}/professionals/${profId}/subscription`); }

  searchUserByEmail(email: string): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/search-user`, { params: this.params({ email }) });
  }

  getProfUsers(profId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.BASE}/professionals/${profId}/users`);
  }

  addProfUser(profId: number, userId: number, role: string, permissions?: string): Observable<any> {
    return this.http.post(`${this.BASE}/professionals/${profId}/users`, { userId, role, permissions });
  }

  removeProfUser(profId: number, userId: number): Observable<any> {
    return this.http.delete(`${this.BASE}/professionals/${profId}/users/${userId}`);
  }

  // Convites
  createInvite(profId: number, email: string, role?: string, permissions?: string): Observable<any> {
    return this.http.post(`${this.BASE}/professionals/${profId}/invites`, { email, role, permissions });
  }

  getInvites(profId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.BASE}/professionals/${profId}/invites`);
  }

  validateInvite(token: string): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/validate-invite/${token}`);
  }

  registerWithInvite(token: string, name: string, phone: string, password: string): Observable<any> {
    return this.http.post(`${this.BASE}/auth/register-with-invite`, { token, name, phone, password });
  }

  // ─── REFERRALS ─────────────────────────────────────────────────
  getReferrals(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/referrals`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  createReferral(profId: number, email: string): Observable<any> {
    return this.http.post<any>(
      `${this.BASE}/professionals/${profId}/referrals`,
      { referredEmail: email }
    );
  }

  deleteReferral(profId: number, referralId: number) {
    return this.http.delete(
      `${this.BASE}/professionals/${profId}/referrals/${referralId}`
    );
  }

  validateReferralToken(token: string): Observable<any> {
    return this.http.get<any>(`${this.BASE}/referrals/validate/${token}`);
  }

  // ─── PATIENTS — paginado ───────────────────────────────────────
  getPatients(profId: number, filter: PatientFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/patients`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getPatient(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/patients/${id}`);
  }

  createPatient(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/patients`, d);
  }

  updatePatient(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/patients/${id}`, d);
  }

  deletePatient(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/patients/${id}`);
  }

  // ─── APPOINTMENTS — paginado ───────────────────────────────────
  getAppointments(profId: number, filter: AppointmentFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/appointments`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getAppointment(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/appointments/${id}`);
  }

  createAppointment(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/appointments`, d);
  }

  updateAppointment(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/appointments/${id}`, d);
  }

  deleteAppointment(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/appointments/${id}`);
  }

  confirmAppointment(profId: number, id: number, confirmed: boolean) {
    return this.http.put(
      `${this.BASE}/professionals/${profId}/appointments/${id}/confirm`,
      { isActive: confirmed }
    );
  }

  // ─── WORKING HOURS ─────────────────────────────────────────────
  getWorkingHours(profId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.BASE}/professionals/${profId}/working-hours`);
  }

  saveWorkingHours(profId: number, hours: any[]): Observable<any[]> {
    return this.http.put<any[]>(`${this.BASE}/professionals/${profId}/working-hours`, hours);
  }

  // ─── PAYMENTS — paginado ───────────────────────────────────────
  getPayments(profId: number, filter: PaymentFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/payments`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getPaymentSummary(profId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.get<any>(
      `${this.BASE}/professionals/${profId}/payments/summary`,
      { params: this.params({ startDate, endDate }) }
    );
  }

  createPayment(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/payments`, d);
  }

  updatePayment(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/payments/${id}`, d);
  }

  deletePayment(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/payments/${id}`);
  }

  // ─── ADMIN — tudo paginado ─────────────────────────────────────
  adminDashboard(): Observable<any> {
    return this.http.get<any>(`${this.BASE}/admin/dashboard`);
  }

  adminUsers(filter: AdminUserFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/admin/users`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  adminToggleUser(id: number, isActive: boolean) {
    return this.http.put(`${this.BASE}/admin/users/${id}/toggle`, { isActive });
  }

  adminProfessionals(filter: AdminProfFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/admin/professionals`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  adminSubscriptions(filter: SubscriptionFilter = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/admin/subscriptions`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  adminPendingPayments(page = 1, pageSize = 20): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/admin/payments/pending`,
      { params: this.params({ page, pageSize }) }
    );
  }

  adminConfirmPayment(id: number) {
    return this.http.put(`${this.BASE}/admin/payments/${id}/confirm`, {});
  }

  adminActivateSub(profId: number)               { return this.http.put(`${this.BASE}/admin/subscriptions/${profId}/activate`, {}); }
  adminCancelSub(profId: number, reason: string) { return this.http.put(`${this.BASE}/admin/subscriptions/${profId}/cancel`, { reason }); }

  // ─── MEDICAL RECORDS ───────────────────────────────────────────
  getMedicalRecords(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/medical-records`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getMedicalRecord(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/medical-records/${id}`);
  }

  createMedicalRecord(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/medical-records`, d);
  }

  updateMedicalRecord(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/medical-records/${id}`, d);
  }

  deleteMedicalRecord(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/medical-records/${id}`);
  }

  addEvolution(profId: number, recordId: number, evolution: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/medical-records/${recordId}/evolutions`, evolution);
  }

  uploadAttachment(profId: number, recordId: number, file: File, description?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/medical-records/${recordId}/attachments`, formData);
  }

  deleteAttachment(profId: number, recordId: number, attachmentId: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/medical-records/${recordId}/attachments/${attachmentId}`);
  }

  exportMedicalRecordPdf(profId: number, recordId: number): Observable<Blob> {
    return this.http.get(`${this.BASE}/professionals/${profId}/medical-records/${recordId}/pdf`, { responseType: 'blob' });
  }

  // ═══════════════════════════════════════════════════════════════
  // FINANCIAL MODULE - ACCOUNTS PAYABLE/RECEIVABLE
  // ═══════════════════════════════════════════════════════════════

  // ─── SUPPLIERS ─────────────────────────────────────────────────
  getSuppliers(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/suppliers`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getSupplier(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/suppliers/${id}`);
  }

  createSupplier(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/suppliers`, d);
  }

  updateSupplier(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/suppliers/${id}`, d);
  }

  deleteSupplier(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/suppliers/${id}`);
  }

  // ─── ACCOUNT CATEGORIES ────────────────────────────────────────
  getAccountCategories(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/account-categories`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getAccountCategory(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/account-categories/${id}`);
  }

  createAccountCategory(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/account-categories`, d);
  }

  updateAccountCategory(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/account-categories/${id}`, d);
  }

  deleteAccountCategory(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/account-categories/${id}`);
  }

  // ─── COST CENTERS ──────────────────────────────────────────────
  getCostCenters(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/cost-centers`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getCostCenter(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/cost-centers/${id}`);
  }

  createCostCenter(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/cost-centers`, d);
  }

  updateCostCenter(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/cost-centers/${id}`, d);
  }

  deleteCostCenter(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/cost-centers/${id}`);
  }

  // ─── BANK ACCOUNTS ─────────────────────────────────────────────
  getBankAccounts(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/bank-accounts`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getBankAccount(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/bank-accounts/${id}`);
  }

  createBankAccount(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/bank-accounts`, d);
  }

  updateBankAccount(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/bank-accounts/${id}`, d);
  }

  deleteBankAccount(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/bank-accounts/${id}`);
  }

  // ─── HEALTH INSURANCES ─────────────────────────────────────────
  getHealthInsurances(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/health-insurances`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getHealthInsurance(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/health-insurances/${id}`);
  }

  createHealthInsurance(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/health-insurances`, d);
  }

  updateHealthInsurance(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/health-insurances/${id}`, d);
  }

  deleteHealthInsurance(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/health-insurances/${id}`);
  }

  // ─── ACCOUNTS RECEIVABLE ───────────────────────────────────────
  getAccountsReceivable(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/accounts-receivable`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getAccountReceivable(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/accounts-receivable/${id}`);
  }

  createAccountReceivable(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/accounts-receivable`, d);
  }

  updateAccountReceivable(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/accounts-receivable/${id}`, d);
  }

  deleteAccountReceivable(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/accounts-receivable/${id}`);
  }

  // ─── ACCOUNTS PAYABLE ──────────────────────────────────────────
  getAccountsPayable(profId: number, filter: any = {}): Observable<PagedResult<any>> {
    return this.http.get<PagedResult<any>>(
      `${this.BASE}/professionals/${profId}/accounts-payable`,
      { params: this.params({ page: 1, pageSize: 20, ...filter }) }
    );
  }

  getAccountPayable(profId: number, id: number): Observable<any> {
    return this.http.get<any>(`${this.BASE}/professionals/${profId}/accounts-payable/${id}`);
  }

  createAccountPayable(profId: number, d: any): Observable<any> {
    return this.http.post<any>(`${this.BASE}/professionals/${profId}/accounts-payable`, d);
  }

  updateAccountPayable(profId: number, id: number, d: any) {
    return this.http.put(`${this.BASE}/professionals/${profId}/accounts-payable/${id}`, d);
  }

  deleteAccountPayable(profId: number, id: number) {
    return this.http.delete(`${this.BASE}/professionals/${profId}/accounts-payable/${id}`);
  }

  // ─── GOOGLE CALENDAR ───────────────────────────────────────────
  getGoogleCalendarStatus(profId: number): Observable<{ connected: boolean; googleEmail: string | null }> {
    return this.http.get<any>(`${this.BASE}/google-calendar/status/${profId}`);
  }

  getGoogleCalendarAuthUrl(profId: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.BASE}/google-calendar/auth-url`, {
      params: new HttpParams().set('professionalId', profId)
    });
  }

  disconnectGoogleCalendar(profId: number): Observable<any> {
    return this.http.delete(`${this.BASE}/google-calendar/disconnect/${profId}`);
  }
}
